import { EVENT_APP_RENDER } from "@/__main__/App.jsx";
import { readState } from "@/__main__/app-state.mjs";
import {
  IS_APP,
  IS_NODE_BROWSERLESS,
  IS_TESTING,
} from "@/__main__/constants.mjs";
import { EVENT_NETWORK_ERROR, postData } from "@/__main__/get-data.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router, { EVENT_CHANGE_ROUTE } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import {
  appURLs,
  EVENT_APP_DOWNLOAD,
  EVENT_FEATURE_VERSION,
  EVENT_HIGH_MEMORY_REFRESH,
  EVENT_PROMO_MODAL_CLOSE,
  EVENT_TIMED_REFRESH,
  EVENT_UNINSTALL_SURVEY,
} from "@/app/constants.mjs";
import { EVENT_ERROR } from "@/app/ErrorBoundary.jsx";
import noopModel from "@/data-models/no-op.mjs";
import {
  EVENT_MATCH_NATIVE_CLICK,
  EVENT_MATCH_NATIVE_VIEW,
  EVENT_TRACK_AD_REPORT,
  EVENT_TRACK_AD_VIDEO_ERROR,
  EVENT_TRACK_AD_VIDEO_VIEW,
  EVENT_TRACK_AD_VIEW,
  EVENT_TRACK_VIDEO_PROVIDER,
} from "@/feature-ads/constants/events.mjs";
import {
  loadBaseInfo,
  loadBrowserInfo,
  saveBaseInfo,
  saveBrowserInfo,
} from "@/feature-analytics/actions.mjs";
import ANALYTIC_EVENTS from "@/feature-analytics/analytic-events.mjs";
import {
  start as startWindowWatcher,
  stop as stopWindowWatcher,
} from "@/feature-analytics/game-windows.mjs";
import {
  onAdReport,
  onAdVideoError,
  onAdVideoView,
  onAdView,
  onMatchtileClick,
  onMatchtileView,
  onVideoProvider,
} from "@/feature-analytics/hooks-ads.mjs";
import {
  onEnterApexGame,
  onExitApexGame,
} from "@/feature-analytics/hooks-apex.mjs";
import {
  onAppDownload,
  onAppError,
  onAppStart,
  onFeatureVersionChange,
  onInteraction,
  onNetworkError,
  onOverlayVisibility,
  onPromoModalClose,
  onRefreshApp,
  onRouteChange,
  onTimedRefreshApp,
  onUninstallSurvey,
} from "@/feature-analytics/hooks-app.mjs";
import {
  trackBattlesAdImpression,
  trackBattlesClick,
} from "@/feature-analytics/hooks-battles.mjs";
import {
  onEnterCsgoGame,
  onExitCsgoGame,
} from "@/feature-analytics/hooks-csgo.mjs";
import {
  onDeadlockEnterGame,
  onDeadlockExitGame,
  onDeadlockHeartbeat,
  onDeadlockMetaData,
} from "@/feature-analytics/hooks-deadlock.mjs";
import {
  onEnterEftGame,
  onExitEftGame,
} from "@/feature-analytics/hooks-eft.mjs";
import {
  onEnterFortniteGame,
  onExitFortniteGame,
  onFortniteItemShare,
  onFortniteMatchLive,
} from "@/feature-analytics/hooks-fn.mjs";
import {
  onAramMayhemPostmatch,
  onArenaPostmatch,
  onBrawlPostmatch,
  onBuildImportFailed,
  onEnterLolGame,
  onExitLolGame,
  onFriends,
  onRunesImportError,
  onRunesImportSuccess,
  onSeasonReview,
  onSpellsImportError,
  onSpellsImportSuccess,
  onTimelineLoaded,
} from "@/feature-analytics/hooks-lol.mjs";
import {
  onEnterMarvelRivalsGame,
  onExitMarvelRivalsGame,
} from "@/feature-analytics/hooks-marvel-rivals.mjs";
import { onMinecraftHeartbeat } from "@/feature-analytics/hooks-minecraft.mjs";
import { onProConversion } from "@/feature-analytics/hooks-premium.mjs";
import {
  onExitRocketGame,
  onRocketLoad,
  onRocketRunning,
} from "@/feature-analytics/hooks-rocket.mjs";
import { onSurveyPostmatch } from "@/feature-analytics/hooks-survey.mjs";
import {
  onEnterTftGame,
  onExitTftGame,
  onLpChangeTft,
  onOverlayUsageTft,
  onPostmatchTft,
  onRoundDataTft,
} from "@/feature-analytics/hooks-tft.mjs";
import {
  onEnterValGame,
  onExitValGame,
  onLoadedValLoadingScreen,
  onMatchStats,
} from "@/feature-analytics/hooks-val.mjs";
import { trackMouseOver } from "@/feature-analytics/mouseover.mjs";
import { onAppRendered } from "@/feature-analytics/performance-timing.mjs";
import {
  start as startResourceTracker,
  stop as stopResourceTracker,
} from "@/feature-analytics/resource-tracker.mjs";
import { trackScrolling } from "@/feature-analytics/scrolling.mjs";
import {
  finalizeProviders,
  initializeProviders,
} from "@/feature-analytics/utils.mjs";
import {
  EVENT_BATTLES_CLICK,
  EVENT_TRACK_BATTLES_AD_IMPRESSION,
} from "@/feature-battles/constants.mjs";
import { EVENT_PRO_CONVERSION } from "@/feature-subscriber/constants/events.mjs";
import { EVENT_USER_SURVEY } from "@/feature-survey/constants.mjs";
import {
  EVENT_APEX_ENTER_GAME,
  EVENT_APEX_EXIT_GAME,
} from "@/game-apex/apex-client-api.mjs";
import {
  EVENT_CSGO_ENTER_GAME,
  EVENT_CSGO_EXIT_GAME,
} from "@/game-csgo/csgo-client-api.mjs";
import {
  EVENT_DEADLOCK_ENTER_GAME,
  EVENT_DEADLOCK_EXIT_GAME,
  EVENT_DEADLOCK_HEARTBEAT,
  EVENT_DEADLOCK_METADATA,
} from "@/game-deadlock/utils/deadlock-client.api.mjs";
import {
  EVENT_EFT_ENTER_GAME,
  EVENT_EFT_EXIT_GAME,
} from "@/game-eft/utils/eft-client-api.mjs";
import {
  EVENT_FORTNITE_ENTER_GAME,
  EVENT_FORTNITE_EXIT_GAME,
  EVENT_FORTNITE_ITEM_SHARE,
  EVENT_FORTNITE_MATCH_LIVE,
} from "@/game-fortnite/constants/events.mjs";
import {
  EVENT_LOL_ARAM_MAYHEM_POSTMATCH,
  EVENT_LOL_ARENA_POSTMATCH,
  EVENT_LOL_BRAWL_POSTMATCH,
  EVENT_LOL_BUILD_IMPORT_FAILED,
  EVENT_LOL_ENTER_GAME,
  EVENT_LOL_EXIT_GAME,
  EVENT_LOL_FRIENDS,
  EVENT_LOL_IMPORT_ERROR_RUNES,
  EVENT_LOL_IMPORT_ERROR_SPELLS,
  EVENT_LOL_IMPORT_SUCCESS_RUNES,
  EVENT_LOL_IMPORT_SUCCESS_SPELLS,
  EVENT_LOL_TIMELINE_LOADED,
  EVENT_TFT_ENTER_GAME,
  EVENT_TFT_EXIT_GAME,
  EVENT_TFT_LP_CHANGE,
  EVENT_TFT_OVERLAY_USAGE,
  EVENT_TFT_POSTMATCH,
  EVENT_TFT_ROUND_DATA,
} from "@/game-lol/constants/constants.mjs";
import { EVENT_SEASON_REVIEW_PROMO_CLICK } from "@/game-lol/constants/season-review.mjs";
import {
  EVENT_MARVEL_RIVALS_ENTER_GAME,
  EVENT_MARVEL_RIVALS_EXIT_GAME,
} from "@/game-marvel-rivals/events.mjs";
import { EVENT_MINECRAFT_HEARTBEAT } from "@/game-minecraft/utils/minecraft-client.api.mjs";
import {
  EVENT_ROCKET_LOAD,
  EVENT_ROCKET_MATCH_END,
  EVENT_ROCKET_RUNNING,
} from "@/game-rocket/utils/rocket-client-api.mjs";
import {
  EVENT_VAL_ENTER_GAME,
  EVENT_VAL_EXIT_GAME,
} from "@/game-val/val-client-api.mjs";
import { EVENT_OVERLAY_VISIBILITY } from "@/hub-overlays/handle-messages.mjs";
import {
  EVENT_VAL_LOADING_SCREEN,
  EVENT_VAL_MATCH_STATS,
} from "@/routes/constants.mjs";
import { devError } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import symbolName from "@/util/symbol-name.mjs";
import { fetchCountryCode } from "@/util/use-country-code.mjs";
import { EVENT_USER_INTERACTION } from "@/util/use-interaction-event.mjs";

const DEFAULT_BASE_INFO_OBJ = {
  accountId: null,
  machineId: null,
  ipLocation: null,
  computerSpecs: null,
};

const handlerMap = [
  // APP
  [eventBus, EVENT_APP_RENDER, onAppStart],
  [eventBus, EVENT_APP_RENDER, trackMouseOver],
  [eventBus, EVENT_APP_RENDER, trackScrolling],
  [eventBus, EVENT_NETWORK_ERROR, onNetworkError],
  [eventBus, EVENT_APP_DOWNLOAD, onAppDownload],
  [eventBus, EVENT_ERROR, onAppError],
  [router.events, EVENT_CHANGE_ROUTE, onRouteChange],
  [eventBus, EVENT_USER_INTERACTION, onInteraction],

  [eventBus, EVENT_UNINSTALL_SURVEY, onUninstallSurvey],
  [eventBus, EVENT_PROMO_MODAL_CLOSE, onPromoModalClose],
  [eventBus, EVENT_HIGH_MEMORY_REFRESH, onRefreshApp],
  [eventBus, EVENT_TIMED_REFRESH, onTimedRefreshApp],
  [eventBus, EVENT_FEATURE_VERSION, onFeatureVersionChange],

  // LOL
  [eventBus, EVENT_LOL_ENTER_GAME, onEnterLolGame],
  [eventBus, EVENT_LOL_EXIT_GAME, onExitLolGame],
  [eventBus, EVENT_LOL_BUILD_IMPORT_FAILED, onBuildImportFailed],
  // LCU import events
  [eventBus, EVENT_LOL_IMPORT_ERROR_SPELLS, onSpellsImportError],
  [eventBus, EVENT_LOL_IMPORT_SUCCESS_SPELLS, onSpellsImportSuccess],
  [eventBus, EVENT_LOL_IMPORT_ERROR_RUNES, onRunesImportError],
  [eventBus, EVENT_LOL_IMPORT_SUCCESS_RUNES, onRunesImportSuccess],

  // Postmatch
  [eventBus, EVENT_LOL_ARENA_POSTMATCH, onArenaPostmatch],
  [eventBus, EVENT_LOL_BRAWL_POSTMATCH, onBrawlPostmatch],
  [eventBus, EVENT_LOL_ARAM_MAYHEM_POSTMATCH, onAramMayhemPostmatch],

  // Misc
  [eventBus, EVENT_SEASON_REVIEW_PROMO_CLICK, onSeasonReview],
  [eventBus, EVENT_LOL_TIMELINE_LOADED, onTimelineLoaded],
  [eventBus, EVENT_LOL_FRIENDS, onFriends],

  // TFT
  [eventBus, EVENT_TFT_ENTER_GAME, onEnterTftGame],
  [eventBus, EVENT_TFT_EXIT_GAME, onExitTftGame],
  [eventBus, EVENT_TFT_LP_CHANGE, onLpChangeTft],
  [eventBus, EVENT_TFT_POSTMATCH, onPostmatchTft],
  [eventBus, EVENT_TFT_ROUND_DATA, onRoundDataTft],
  [eventBus, EVENT_TFT_OVERLAY_USAGE, onOverlayUsageTft],

  // VAL
  [eventBus, EVENT_VAL_ENTER_GAME, onEnterValGame],
  [eventBus, EVENT_VAL_EXIT_GAME, onExitValGame],
  [eventBus, EVENT_VAL_LOADING_SCREEN, onLoadedValLoadingScreen],
  [eventBus, EVENT_VAL_MATCH_STATS, onMatchStats],

  // APEX
  [eventBus, EVENT_APEX_ENTER_GAME, onEnterApexGame],
  [eventBus, EVENT_APEX_EXIT_GAME, onExitApexGame],

  // CSGO
  [eventBus, EVENT_CSGO_ENTER_GAME, onEnterCsgoGame],
  [eventBus, EVENT_CSGO_EXIT_GAME, onExitCsgoGame],

  // FORTNITE
  [eventBus, EVENT_FORTNITE_ENTER_GAME, onEnterFortniteGame],
  [eventBus, EVENT_FORTNITE_EXIT_GAME, onExitFortniteGame],
  [eventBus, EVENT_FORTNITE_MATCH_LIVE, onFortniteMatchLive],
  [eventBus, EVENT_FORTNITE_ITEM_SHARE, onFortniteItemShare],

  // EFT
  [eventBus, EVENT_EFT_ENTER_GAME, onEnterEftGame],
  [eventBus, EVENT_EFT_EXIT_GAME, onExitEftGame],

  // DEADLOCK
  [eventBus, EVENT_DEADLOCK_HEARTBEAT, onDeadlockHeartbeat],
  [eventBus, EVENT_DEADLOCK_ENTER_GAME, onDeadlockEnterGame],
  [eventBus, EVENT_DEADLOCK_EXIT_GAME, onDeadlockExitGame],
  [eventBus, EVENT_DEADLOCK_METADATA, onDeadlockMetaData],

  // MARVEL RIVALS
  [eventBus, EVENT_MARVEL_RIVALS_ENTER_GAME, onEnterMarvelRivalsGame],
  [eventBus, EVENT_MARVEL_RIVALS_EXIT_GAME, onExitMarvelRivalsGame],

  // MINECRAFT
  [eventBus, EVENT_MINECRAFT_HEARTBEAT, onMinecraftHeartbeat],

  // ROCKET LEAGUE
  [eventBus, EVENT_ROCKET_RUNNING, onRocketRunning],
  [eventBus, EVENT_ROCKET_LOAD, onRocketLoad],
  [eventBus, EVENT_ROCKET_MATCH_END, onExitRocketGame],

  // BATTLES
  [eventBus, EVENT_BATTLES_CLICK, trackBattlesClick],
  [eventBus, EVENT_TRACK_BATTLES_AD_IMPRESSION, trackBattlesAdImpression],

  // OVERLAYS
  [eventBus, EVENT_OVERLAY_VISIBILITY, onOverlayVisibility],

  // ADS
  [eventBus, EVENT_TRACK_AD_VIEW, onAdView],
  [eventBus, EVENT_TRACK_AD_REPORT, onAdReport],
  [eventBus, EVENT_TRACK_AD_VIDEO_VIEW, onAdVideoView],
  [eventBus, EVENT_TRACK_AD_VIDEO_ERROR, onAdVideoError],
  [eventBus, EVENT_TRACK_VIDEO_PROVIDER, onVideoProvider],
  [eventBus, EVENT_MATCH_NATIVE_CLICK, onMatchtileClick],
  [eventBus, EVENT_MATCH_NATIVE_VIEW, onMatchtileView],

  // PREMIUM
  [eventBus, EVENT_PRO_CONVERSION, onProConversion],

  // SURVEY
  [eventBus, EVENT_USER_SURVEY, onSurveyPostmatch],
];

const env = {
  baseInfo: Object.assign({}, DEFAULT_BASE_INFO_OBJ),
  setupPromise: null,
  providers: [
    {
      name: "events_tracker",
      web: true,
      app: true,
      initialize() {
        initComputerSpec();
        initCountryCode();

        handlerMap.forEach((one) => {
          const [target, evt, fn] = one;
          target.on(evt, fn);
        });
      },
      finalize() {
        handlerMap.forEach((one) => {
          const [target, evt, fn] = one;
          target.off(evt, fn);
        });
      },
    },
    {
      name: "game_window_tracker",
      web: false,
      app: true,
      initialize() {
        startWindowWatcher();
      },
      finalize() {
        stopWindowWatcher();
      },
    },
    {
      name: "resource_tracker",
      web: false,
      app: true,
      initialize() {
        startResourceTracker();
      },
      finalize() {
        stopResourceTracker();
      },
    },
  ],
  memoizedIdentifier: null,
};

if (!IS_APP) {
  [[eventBus, EVENT_APP_RENDER, onAppRendered]].forEach((x) =>
    handlerMap.push(x),
  );
}

export async function setup() {
  await loadBaseInfo(env.baseInfo);
  if (!env.setupPromise) {
    env.setupPromise = initializeProviders(env.providers);
  }
  return env.setupPromise;
}

export function teardown() {
  finalizeProviders(env.providers);
  env.setupPromise = null;
}

const isImmediate = symbolName("event-is-immediate");
const temporaryTimeStamp = symbolName("event-timestamp");

export const refs__PRIVATE = {
  trackEvent,
  isImmediate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  gtag: (...args) => null,
  getIdentifyingInfo,
};

globals.__BLITZ_DEV__.trackEvent = trackEvent;

async function getFingerprint(anonymous = false) {
  if (IS_APP) {
    if (!env.memoizedIdentifier) {
      env.memoizedIdentifier = await blitzMessage(EVENTS.MACHINE_ID);
      env.baseInfo.machineId = env.memoizedIdentifier;

      // The core version can be different...
      // env.baseInfo.appVersion = await blitzMessage(EVENTS.APP_VERSION);

      env.baseInfo.appVersion = mainRefs.PKG_VERSION;
    }

    if (anonymous) {
      if (!env.memoizedIdentifierHashed) {
        env.memoizedIdentifierHashed = await blitzMessage(EVENTS.MACHINE_ID, {
          hash: true,
          algo: "sha256",
        });

        env.baseInfo.machineId = env.memoizedIdentifierHashed;
      }
    }
  } else if (!env.memoizedIdentifier) {
    let { fingerprint } = (await loadBrowserInfo()) ?? {};
    if (!fingerprint) {
      const { default: getBrowserFingerprint } = await import(
        "get-browser-fingerprint"
      );
      fingerprint = await getBrowserFingerprint();
      saveBrowserInfo({ fingerprint });
    }

    env.memoizedIdentifier = fingerprint;

    env.baseInfo.browserId = env.memoizedIdentifier;
    env.baseInfo.appVersion = "web";

    // TODO: use WebCrypto to hash the fingerprint for anonymous events
    env.memoizedIdentifierHashed = null;
  }

  return !anonymous ? env.memoizedIdentifier : env.memoizedIdentifierHashed;
}

async function getIdentifyingInfo(anonymous = false) {
  const fingerprint = await getFingerprint(anonymous);

  let info;

  if (IS_APP) {
    info = {
      machineId: fingerprint,
      appVersion: env.baseInfo.appVersion,
    };
  } else {
    info = {
      browserId: fingerprint,
      appVersion: env.baseInfo.appVersion,
    };
  }

  if (!anonymous && readState?.user) {
    info.accountId = readState.user.id;
  }

  return info;
}

async function trackEvent(eventName, eventData, anonymous = false) {
  if (IS_TESTING || IS_NODE_BROWSERLESS) return null;

  const info = await getIdentifyingInfo(anonymous);

  const event = {
    name: eventName,
    platform: IS_APP ? "app" : "web", // CHECK: We may want to track overlay's separately
    ...info,
  };

  const eventPayload = {
    event,
    [temporaryTimeStamp]: Date.now(),
  };

  if (eventData) {
    eventPayload.data = eventData;
  }

  return sendTrackEvent(eventPayload);
}

// Rather than send an individual event right away, we will throttle the actual
// POST request. The first event will begin throttling, then we will batch all
// subsequent events until time's up.
const EVENT_THROTTLE_TIME = IS_APP ? 25 * 1000 : 5 * 1000;
const eventQueue = [];
let trackEventTimeout = null;

function sendTrackEvent(eventPayload) {
  const sendImmediate = eventPayload?.["data"]?.[isImmediate];

  // Not necessary to delete, symbols aren't serialized in JSON.
  // if (sendImmediate) delete eventPayload["data"][isImmediate];

  eventQueue.push(eventPayload);

  if (sendImmediate) {
    return processEventQueue();
  }

  if (trackEventTimeout) return null;
  trackEventTimeout = setTimeout(processEventQueue, EVENT_THROTTLE_TIME);
}

async function processEventQueue() {
  clearTimeout(trackEventTimeout);
  trackEventTimeout = null;

  const eventsToFit = [];
  let totalBytes = 0;
  // the limit of 64KB is defined in https://github.com/theblitzapp/blitz-analytics-batch-server/blob/master/src/main.rs#L63C29-L63C38
  // + leave some room for the JSON structure
  const REQ_BODY_LIMIT = (64 - 1) * 1024;

  while (eventQueue.length) {
    const event = eventQueue[eventQueue.length - 1];

    // This is a duration in milliseconds to signal to the server how long ago
    // this event was emitted before the batch submission time.
    event.event.timeDelta =
      Date.now() - (event[temporaryTimeStamp] ?? Date.now());

    const eventString = JSON.stringify(event);
    const eventBytes = new globals.Blob([eventString]).size;

    if (totalBytes + eventBytes > REQ_BODY_LIMIT) {
      if (!trackEventTimeout)
        trackEventTimeout = setTimeout(processEventQueue, EVENT_THROTTLE_TIME);
      break;
    }

    eventsToFit.push(eventQueue.pop());
    totalBytes += eventBytes;
  }

  const headers = {
    "content-type": "application/json",
  };
  let body = JSON.stringify(eventsToFit);

  // if the body is worth compressing, do it.
  if (body.length > 4096) {
    // This is just a quirk of the analytics batch server.
    headers["content-type"] = null;
    headers["content-encoding"] = "gzip";

    const blob = new globals.Blob([body]);

    body = blob.stream().pipeThrough(new globals.CompressionStream("gzip"));

    // To avoid passing in a ReadableStream and having to define "duplex"
    // parameter, we're just going to blobify.
    body = await new globals.Response(body).blob();
  }

  try {
    await postData(
      {
        url: appURLs.TRACK_EVENTS,
        body,
      },
      noopModel,
      undefined,
      {
        headers,
      },
    );

    return true;
  } catch (err) {
    devError("FAILED TO POST ANALYTICS", err);

    return false;
  }
}

async function initComputerSpec() {
  const computerSpecs = await blitzMessage(EVENTS.MACHINE_INFO);
  env.baseInfo.computerSpecs = computerSpecs;
  saveBaseInfo(env.baseInfo);
  refs__PRIVATE.trackEvent(ANALYTIC_EVENTS.USER_SPEC_UPDATE, computerSpecs);
}

async function initCountryCode() {
  const countryCode = await fetchCountryCode({ skipSafetyCheck: true });

  // TEMP: send country kode on init.
  refs__PRIVATE.trackEvent(ANALYTIC_EVENTS.USER_LOC_UPDATE, { countryCode });

  if (env.baseInfo.ipLocation?.countryCode === countryCode) return;

  // Location changed
  env.baseInfo.ipLocation = { countryCode };
  saveBaseInfo(env.baseInfo);
}
